import type {
  ChannelCapability,
  Faq,
  ImmutableApprovalSnapshot,
  Provider,
} from "./types";

const SENSITIVE_PATTERNS = [
  /\b(emergency|suicide|self[- ]?harm|diagnos(?:e|is)|dosage|dose|prescription|pregnan|bleeding|chest pain)\b/iu,
  /(အရေးပေါ်|သတ်သေ|ကိုယ့်ကိုယ်ကို|ရောဂါရှာ|ဆေးပမာဏ|ဆေးညွှန်း|ကိုယ်ဝန်|သွေးထွက်|ရင်ဘတ်နာ)/u,
];

const PROMPT_INJECTION_PATTERNS = [
  /ignore (all|any|the) previous/iu,
  /system prompt/iu,
  /developer message/iu,
  /override (the )?(rules|instructions)/iu,
  /(အရင်ညွှန်ကြားချက်|စည်းမျဉ်း).*(မလိုက်နာ|မေ့|ပယ်)/u,
];

export function normalizeQuestion(value: string): string {
  return value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[?!။၊.,:;"'“”‘’()[\]{}]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

export function isSensitiveQuestion(value: string): boolean {
  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(value));
}

export function containsPromptInjection(value: string): boolean {
  return PROMPT_INJECTION_PATTERNS.some((pattern) => pattern.test(value));
}

export function matchApprovedFaq(value: string, faqs: Faq[]): Faq | null {
  if (isSensitiveQuestion(value) || containsPromptInjection(value)) return null;

  const normalized = normalizeQuestion(value);
  if (!normalized) return null;

  const matches = faqs.filter((faq) => {
    if (!faq.active || !faq.approved || faq.sensitive) return false;
    return [faq.question, ...faq.variants]
      .map(normalizeQuestion)
      .includes(normalized);
  });

  return matches.length === 1 ? matches[0] ?? null : null;
}

export function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;

  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`);
  return `{${entries.join(",")}}`;
}

export function validateApprovalSnapshot(
  value: ImmutableApprovalSnapshot,
): string | null {
  if (!value.draftId || !value.channelId || !value.destinationId || !value.body.trim()) {
    return "Draft, channel and body are required.";
  }
  if (!Number.isSafeInteger(value.revision) || !Number.isSafeInteger(value.contextVersion) || value.revision < 1 || value.contextVersion < 1) {
    return "Approval revision is invalid.";
  }
  if (Number.isNaN(Date.parse(value.scheduledFor))) {
    return "Schedule time is invalid.";
  }
  return null;
}

export function defaultCapabilities(provider: Provider): ChannelCapability[] {
  if (provider === "facebook") {
    return [
      { name: "inbox_read", status: "pending_approval", reason: "Meta App Review must be verified for the new AutoSocial app." },
      { name: "message_reply", status: "pending_approval", reason: "Requires pages_messaging and a valid 24-hour response window." },
      { name: "comments_read", status: "pending_approval" },
      { name: "comment_reply", status: "pending_approval" },
      { name: "content_publish", status: "pending_approval" },
      { name: "content_schedule", status: "pending_approval" },
      { name: "video_upload", status: "pending_approval" },
    ];
  }

  if (provider === "youtube") {
    return [
      { name: "inbox_read", status: "unavailable", reason: "YouTube provides no documented channel inbox API." },
      { name: "message_reply", status: "unavailable", reason: "YouTube provides no documented private-message reply API." },
      { name: "comments_read", status: "pending_approval", reason: "Connect and verify YouTube OAuth permissions first (M6)." },
      { name: "comment_reply", status: "pending_approval", reason: "Connect and verify YouTube OAuth permissions first (M6)." },
      { name: "content_publish", status: "pending_approval", reason: "New API projects upload private-only until the YouTube compliance audit passes." },
      { name: "content_schedule", status: "pending_approval", reason: "Scheduling is available only after an eligible private upload." },
      { name: "video_upload", status: "pending_approval", reason: "Upload integration and OAuth verification are not yet implemented (M6)." },
    ];
  }

  return [
    { name: "inbox_read", status: "pending_approval", reason: "TikTok Business Messaging access and data-security review are required." },
    { name: "message_reply", status: "pending_approval", reason: "TikTok Business Messaging approval is required." },
    { name: "comments_read", status: "pending_approval", reason: "TikTok Business API account-comment access is required." },
    { name: "comment_reply", status: "pending_approval" },
    { name: "content_publish", status: "blocked", reason: "TikTok does not accept an internal-only Direct Post utility.", nextAction: "Keep this blocked until AutoSocial has a public SaaS audience and passes audit." },
    { name: "content_schedule", status: "blocked", reason: "Public posting eligibility has not been approved." },
    { name: "video_upload", status: "blocked", reason: "Unaudited internal posting is private-only and not suitable for this release." },
  ];
}
